import { Box, Button, Drawer, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import React from 'react';
import { LinkPageOutlink } from 'common-ts/dist/models/LinkPage';
import { ProductOnUser } from 'common-ts/dist/models/Product';
import { formatRupiah, marketplaceComparison } from './LinkPageShared';

// Exit-interception dialog for marketplace outlinks on the /link page:
// before letting the visitor leave, show what buying direct saves them.
// Interception is click-level only — back/close can't be blocked.

const useStyles = makeStyles((theme: Theme) =>
createStyles({
  sheet: {
    borderRadius: '16px 16px 0px 0px',
    padding: '22px 20px 26px',
    maxWidth: '480px',
    margin: '0 auto',
  },
  title: {
    fontSize: '18px',
    fontWeight: 700,
    marginBottom: '14px',
  },
  compareRow: {
    display: 'flex',
    gap: '10px',
    marginBottom: '16px',
  },
  compareCard: {
    flex: 1,
    borderRadius: '12px',
    padding: '12px',
    textAlign: 'center',
  },
  marketplaceCard: {
    border: '1px solid #e0e0e0',
    backgroundColor: '#f7f7f7',
  },
  directCard: {
    border: '1px solid #0a7d43',
    backgroundColor: '#e7f6ee',
  },
  compareLabel: {
    fontSize: '11px',
    color: '#6c6c6c',
    marginBottom: '4px',
  },
  comparePrice: {
    fontSize: '17px',
    fontWeight: 700,
  },
  marketplacePriceText: {
    textDecoration: 'line-through',
    color: '#8a8a8a',
  },
  hematText: {
    fontSize: '12px',
    fontWeight: 700,
    color: '#0a7d43',
    marginTop: '4px',
  },
  genericCopy: {
    fontSize: '14px',
    color: '#4a4a4a',
    marginBottom: '16px',
  },
  stayButton: {
    width: '100%',
    padding: '12px 0px',
    borderRadius: '10px',
    fontWeight: 700,
    marginBottom: '10px',
  },
  leaveButton: {
    width: '100%',
    fontSize: '12px',
    color: '#9a9a9a',
    textTransform: 'none',
  },
}),
);

interface LinkPageExitDialogProps {
  // The intercepted outlink; null = closed.
  link: LinkPageOutlink | null;
  // Cheapest carousel product that has an honest marketplace comparison —
  // the concrete example shown in the dialog. null = generic copy.
  comparisonProduct: ProductOnUser | null;
  onClose: () => void;
  // "Beli Langsung di Sini": parent scrolls to the carousel (or opens
  // buy-now directly when there is a single product).
  onStay: () => void;
}

export default function LinkPageExitDialog({ link, comparisonProduct, onClose, onStay }: LinkPageExitDialogProps) {
  const classes = useStyles();

  const comparison = comparisonProduct ? marketplaceComparison(comparisonProduct) : null;

  return (
    <Drawer
      anchor="bottom"
      open={!!link}
      onClose={onClose}
      PaperProps={{ className: classes.sheet }}
    >
      <Typography className={classes.title}>
        Yakin mau beli di marketplace?
      </Typography>
      {
        comparison && comparisonProduct ?
        <>
          <Box className={classes.compareRow}>
            <Box className={classes.compareCard + ' ' + classes.marketplaceCard}>
              <Typography className={classes.compareLabel}>Di Marketplace</Typography>
              <Typography className={classes.comparePrice + ' ' + classes.marketplacePriceText}>
                {formatRupiah(comparison.hargaMarketplace)}
              </Typography>
            </Box>
            <Box className={classes.compareCard + ' ' + classes.directCard}>
              <Typography className={classes.compareLabel}>Langsung di Sini</Typography>
              <Typography className={classes.comparePrice}>
                {formatRupiah(comparison.hargaLangsung)}
              </Typography>
              <Typography className={classes.hematText}>
                Hemat {formatRupiah(comparison.hemat)} ({comparison.hematPercent}%)
              </Typography>
            </Box>
          </Box>
          <Typography className={classes.genericCopy}>
            Contoh: {comparisonProduct.name}. Belanja langsung di sini selalu lebih murah.
          </Typography>
        </> :
        <Typography className={classes.genericCopy}>
          Harga di website kami selalu lebih murah daripada di marketplace — tanpa
          biaya layanan tambahan.
        </Typography>
      }
      <Button
        className={classes.stayButton}
        variant="contained"
        color="primary"
        disableElevation
        onClick={onStay}
      >
        Beli Langsung di Sini
      </Button>
      <Button
        className={classes.leaveButton}
        onClick={() => {
          if (link) window.open(link.url, '_blank', 'noopener,noreferrer');
          onClose();
        }}
      >
        Tetap ke Marketplace
      </Button>
    </Drawer>
  );
}
